/*
 * @Author: Teemo Ding
 * @Date: 2021-08-06 11:02:17
 * @LastEditors: Json Chen
 * @LastEditTime: 2022-06-07 11:02:24
 * @FilePath: /unifyxp-v2-store-portal/src/pages/CustomerDatabaseConfig/CustomerDatabaseSettings/index.tsx
 */

import { Skeleton } from '@/src/components/Antd';
import CommonHeader from '@/src/components/CommonHeader';
import {
  request_customerDatabaseSettings,
  request_saveCustomerDatabaseSettings,
} from '@/src/request/customer/databaseSettings';
import { randomNumber } from '@/src/tools/helper';
import { Form, message } from 'antd';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useI18n } from 'src/hooks/useRouterChange';
import { isUserValid, storeResourceNameGenerator } from 'src/tools/getUserInfo';
import { i18nGet } from 'src/tools/tools';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from 'src/types/common/resources';
import AdvancedCustomerInformation from './AdvancedCustomerInformation';
import AdvancedInputFieldValidation from './AdvancedInputFieldValidation';
import CustomerTitle from './CustomerTitle';
import FullNameAutoPopulate from './FullNameAutoPopulate';
import Gender from './Gender';
import ThirdPartyLogin from './ThirdPartyLogin';
import { breadcrumbData } from './config';
import css from './css.module.less';

enum ProviderType {
  Facebook = 'Facebook',
  Google = 'Google',
  Microsoft = 'Microsoft',
  Apple = 'Apple',
}
interface IParams {
  customerDatabaseUuid: string;
}

const permissions = () => {
  const view_customer_database_settings = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Customers,
        subCategory: EnumSubCategory.CustomerDatabaseSetting,
        operation: EnumOperation.view_customer_database_settings,
      }),
    ],
  });
  const edit_and_save = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Customers,
        subCategory: EnumSubCategory.CustomerDatabaseSetting,
        operation: EnumOperation.edit_and_save,
      }),
    ],
  });
  return {
    view_customer_database_settings,
    edit_and_save,
  };
};

const CustomerDatabaseSettings = () => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const { view_customer_database_settings, edit_and_save } = permissions();
  const [form] = Form.useForm();
  const history = useHistory();
  const params: IParams = useParams();
  const [loading, setLoading] = useState(true);
  const [hasModified, setHasModified] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [available, setAvailable] = useState([]);
  const [salesChannelDomains, setSalesChannelDomains] = useState('');

  const { customerDatabaseUuid } = params;

  useEffect(() => {
    if (view_customer_database_settings) {
      request_customerDatabaseSettings(customerDatabaseUuid)
        .then((data) => {
          //初始化所有genders为关闭状态
          data.genders.forEach((gender) => {
            gender.open = false;
            gender.uuid = randomNumber();
            return gender;
          });
          data.titles.forEach((title) => {
            title.open = false;
            title.uuid = randomNumber();
            return title;
          });
          setAvailable(data.advancedFields);
          setSalesChannelDomains(data.salesChannelDomains);
          setFormData(data || {});
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const onValuesChange = () => {
    setHasModified(true);
  };

  const onGoBackClick = () => {
    history.push(`/customerDatabase`);
  };
  const onFinish = (values) => {
    const { firstNameFirst, separator } = values;
    values.name = (formData || {}).name || '';
    values.uuid = customerDatabaseUuid;
    values.advancedFields = available;
    values.fullNameAutoPopulate = {
      firstNameFirst,
      separator,
    };
    const providerSettings = [];
    Object.keys(ProviderType).map((key) => {
      providerSettings.push({
        provider: key,
        enabled: values[`${key}_enabled`],
        clientID: values[`${key}_clientID`],
      });
    });
    values.thirdPartyLoginSetting = {
      providerSettings,
    };
    request_saveCustomerDatabaseSettings(values).then((data) => {
      message.success(
        getI18n('customerDatabaseSetting.customerDatabaseSettings.save_succ'),
      );
      setHasModified(false);
    });
  };

  const onDeleteAvailable = (index) => {
    const availableCopy = _.cloneDeep(available);
    availableCopy.splice(index, 1);
    setAvailable(availableCopy);
    setHasModified(true);
  };

  const onEditAvailable = (value, index) => {
    const availableCopy = _.cloneDeep(available);
    availableCopy[index] = value;
    setAvailable(availableCopy);
    setHasModified(true);
  };
  const onAddAvailable = (value) => {
    const availableCopy = _.cloneDeep(available);
    availableCopy.push(value);
    setAvailable(availableCopy);
    setHasModified(true);
  };
  const { fullNameAutoPopulate = {}, thirdPartyLoginSetting = {} } =
    formData || ({} as any);
  const providerSettingsObj = {};
  (thirdPartyLoginSetting.providerSettings || []).forEach((setting) => {
    providerSettingsObj[`${setting.provider}_enabled`] = setting.enabled;
    providerSettingsObj[`${setting.provider}_clientID`] = setting.clientID;
  });
  return (
    <div className={css.container}>
      <Skeleton loading={loading} active>
        <Form
          name={'languageSetting'}
          form={form}
          onFinish={onFinish}
          initialValues={{
            ...formData,
            ...providerSettingsObj,
            firstNameFirst: fullNameAutoPopulate.firstNameFirst,
            separator: fullNameAutoPopulate.separator || 'Space',
          }}
          onValuesChange={onValuesChange}
          className={css.editForm}>
          <CommonHeader
            breadcrumbData={breadcrumbData(
              (formData || {}).name,
              customerDatabaseUuid,
              { getI18n },
            )}
            onSaveDisable={!hasModified || !edit_and_save}
            onSubmit={() => form.submit()}
            onDiscard={onGoBackClick}
            onGoBackClick={onGoBackClick}
          />
          <div className={css.customerDatabaseSettingContainer}>
            <ThirdPartyLogin
              salesChannelDomains={salesChannelDomains}
              disabled={!edit_and_save}
            />
            <FullNameAutoPopulate disabled={!edit_and_save} />
            <Gender form={form} disabled={!edit_and_save} />
            <CustomerTitle form={form} disabled={!edit_and_save} />
            <AdvancedCustomerInformation
              available={available}
              onAddAvailable={onAddAvailable}
              onEditAvailable={onEditAvailable}
              onDeleteAvailable={onDeleteAvailable}
              disabled={!edit_and_save}
            />
            <AdvancedInputFieldValidation disabled={!edit_and_save} />
          </div>
        </Form>
      </Skeleton>
    </div>
  );
};

export default CustomerDatabaseSettings;
